import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Package, Users, ClipboardList, AlertTriangle, TrendingUp, Warehouse } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EnviaBalanceWidget } from '../../components/EnviaBalanceWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ndrPending, setNdrPending] = useState(0);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`, { headers }).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
    axios.get(`${API}/envia/admin/ndr`, { headers })
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        setNdrPending(list.filter(c => !c.ndr_action).length);
      }).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="animate-pulse space-y-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-secondary rounded-lg" />)}</div>;
  if (!data) return <p className="text-muted-foreground">Error al cargar dashboard</p>;

  const s = data.stats;
  const stats = [
    { label: 'Total Productos', value: s.total_products, icon: Package, color: 'text-foreground' },
    { label: 'Productos Activos', value: s.active_products, icon: TrendingUp, color: 'text-foreground' },
    { label: 'Agotados', value: s.out_of_stock, icon: AlertTriangle, color: 'text-destructive' },
    { label: 'Stock Bajo', value: s.low_stock, icon: Warehouse, color: 'text-amber-400' },
    { label: 'Total Usuarios', value: s.total_users, icon: Users, color: 'text-foreground' },
    { label: 'Total Órdenes', value: s.total_orders, icon: ClipboardList, color: 'text-foreground' },
    { label: 'Órdenes Pendientes', value: s.pending_orders, icon: ClipboardList, color: 'text-amber-400' },
    { label: 'Compras Internas', value: s.internal_orders, icon: ClipboardList, color: 'text-foreground' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-display font-semibold">Dashboard</h1>

      {/* Envia.com balance monitor */}
      <EnviaBalanceWidget />

      {ndrPending > 0 && (
        <Link to="/admin/ndr" className="block" data-testid="dashboard-ndr-alert">
          <Card className="border-red-500/40 bg-red-500/5 hover:bg-red-500/10 transition-colors duration-150">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-2.5 rounded-md bg-red-500/15"><AlertTriangle className="h-5 w-5 text-red-300" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-sm text-red-200">
                  {ndrPending} entrega{ndrPending === 1 ? '' : 's'} fallida{ndrPending === 1 ? '' : 's'} pendiente{ndrPending === 1 ? '' : 's'} de acción
                </p>
                <p className="text-xs text-red-300/70">Revisa el panel de NDR y toma una decisión: reagendar, retornar o abandonar.</p>
              </div>
              <Badge variant="outline" className="bg-red-500/15 text-red-300 border-red-500/30 tabular-nums">{ndrPending}</Badge>
            </CardContent>
          </Card>
        </Link>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(st => (
          <Card key={st.label}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <st.icon className={`h-4 w-4 ${st.color}`} />
              </div>
              <p className="text-2xl font-display font-semibold tabular-nums">{st.value}</p>
              <p className="text-xs text-muted-foreground">{st.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <h3 className="font-display font-semibold mb-4">Órdenes Recientes</h3>
            {data.recent_orders?.length === 0 ? <p className="text-sm text-muted-foreground">Sin órdenes</p> : (
              <div className="space-y-3">
                {data.recent_orders?.map(o => (
                  <Link to={`/admin/ordenes/${o.id}`} key={o.id} className="flex items-center justify-between py-2 border-b last:border-0 hover:bg-secondary/30 rounded px-2 transition-colors duration-150">
                    <div>
                      <p className="text-sm font-medium">{o.order_number}</p>
                      <p className="text-xs text-muted-foreground">{o.user_name} - {new Date(o.created_at).toLocaleDateString('es-MX')}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-xs">{o.order_status}</Badge>
                      <p className="text-sm font-semibold mt-1">${o.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="font-display font-semibold mb-4">Alertas de Stock</h3>
            {data.low_stock_products?.length === 0 ? <p className="text-sm text-muted-foreground">Sin alertas</p> : (
              <div className="space-y-3">
                {data.low_stock_products?.map(p => (
                  <Link to="/admin/productos" key={p.id} className="flex items-center justify-between py-2 border-b last:border-0 hover:bg-secondary/30 rounded px-2 transition-colors duration-150">
                    <div>
                      <p className="text-sm font-medium">{p.name}</p>
                      <p className="font-mono text-xs text-muted-foreground">{p.sku}</p>
                    </div>
                    <Badge variant={p.stock === 0 ? 'destructive' : 'outline'}>{p.stock === 0 ? 'Agotado' : `Stock: ${p.stock}`}</Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
